import { useTranslation } from 'react-i18next';

import ActivityPendenciesFilterBar from '@/components/shared/pendencies/ActivityPendenciesFilterBar';
import ActivityPendenciesTable from '@/components/shared/pendencies/tables/ActivityPendenciesTable';
import ResultsCount from '@/components/shared/ResultsCount';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { PaginationControl } from '@/components/ui/daisy';
import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import useAuth from '@/data/hook/useAuth';
import useListService from '@/data/hook/useListService';
import usePageErrorReset from '@/data/hook/shared/usePageErrorReset';
import usePagination from '@/data/hook/shared/usePagination';
import useActivityPendenciesFilter from '@/data/hook/useActivityPendenciesFilter';
import { activityProgressQueries } from '@/data/query-keys/activityProgressQueryKey';

export default function ActivityPendenciesTab() {
  const { t } = useTranslation('translation', { keyPrefix: 'pendencies' });
  const { user } = useAuth();

  const {
    filters,
    mainFilters,
    advancedFilters,
    advancedFiltersCount,
    toggleOrdering,
    updateMainFilters,
    updateAdvancedFilters,
    clearAdvancedFilters,
  } = useActivityPendenciesFilter();
  const { pageNumber, pageSize, firstPage, setPageNumber } = usePagination();

  const { queryKey, queryFn } = activityProgressQueries.list({
    ...filters,
    pageNumber,
    pageSize,
  });

  const {
    results: pendingActivities,
    meta,
    isInitialLoading,
    error,
  } = useListService({
    queryKey,
    queryFn,
    enabled: !!user,
    keepPreviousData: true,
    staleTime,
  });

  const currentPage = pageNumber;
  const totalPages = meta?.pagination.pages ?? 1;
  const totalResults = meta?.pagination.count ?? 0;

  usePageErrorReset({ error, pageNumber, firstPage, setPageNumber });

  return (
    <div className="flex flex-col gap-6">
      <ActivityPendenciesFilterBar
        mainFilters={mainFilters}
        advancedFilters={advancedFilters}
        advancedFiltersCount={advancedFiltersCount}
        updateMainFilters={updateMainFilters}
        updateAdvancedFilters={updateAdvancedFilters}
        clearAdvancedFilters={clearAdvancedFilters}
      />

      <ResultsCount
        className="self-end"
        pageNumber={pageNumber}
        pageSize={pageSize}
        total={totalResults}
      />

      <ActivityPendenciesTable
        ordering={filters.ordering}
        onToggleOrdering={toggleOrdering}
        data={pendingActivities}
        isLoading={isInitialLoading}
        error={error}
        emptyMessage={t('empty')}
        classNames={{
          container: 'rounded-2xl border border-base-content/5 bg-base-200',
          header: 'text-xs',
        }}
      />

      <ConditionalRenderer condition={totalPages > 1}>
        <PaginationControl
          className="self-end"
          color="primary"
          variant="soft"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setPageNumber}
          showPrevNext={false}
        />
      </ConditionalRenderer>
    </div>
  );
}
